import React, { useId, useRef, useState } from 'react';
import ZoomableImage from './ZoomableImage';
import { useOverflowing } from './useOverflowing';

export interface FeatureResponse {
  text: string;
  imageUrl?: string;
  updatedAt: string;
}

// "Oct 9", or "Oct 9, 2025" when it is not this year, in the reader's own time.
export function updateDate(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (d.getFullYear() !== now.getFullYear()) options.year = 'numeric';
  return d.toLocaleDateString('en-US', options);
}

interface Props {
  response: FeatureResponse;
  title: string;
  onEdit?: () => void;
}

// The developer update on a checklist request (#1008), read-only. Closed, its text is
// cut to two lines with the screenshot as a small preview beside it; opened, the full
// text with the screenshot at the box's width. A click on the screenshot opens it large.
const FeatureResponseView: React.FC<Props> = ({ response, title, onEdit }) => {
  const [expanded, setExpanded] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const overflowing = useOverflowing(textRef, [response.text, expanded]);
  const id = useId();
  const date = updateDate(response.updatedAt);
  return (
    <div className="feature-response">
      <div className="feature-response-head">
        <p className="feature-response-label">
          Developer update{date && <span> · {date}</span>}
        </p>
        {onEdit && <button type="button" className="feature-response-link feature-response-edit" onClick={onEdit}>Edit</button>}
      </div>
      <div id={id} className={expanded ? 'feature-response-body feature-response-body--open' : 'feature-response-body'}>
        <p ref={textRef} className={expanded ? 'feature-response-text' : 'feature-response-text expandable-text--clamped'}>
          {response.text}
        </p>
        {response.imageUrl && (
          <div className={expanded ? 'feature-response-shot' : 'feature-response-thumb'}>
            <ZoomableImage src={response.imageUrl} alt={`Screenshot for ${title}`} />
          </div>
        )}
      </div>
      {(expanded || overflowing || !!response.imageUrl) && (
        <button
          type="button"
          className="feature-response-link feature-response-toggle"
          aria-expanded={expanded}
          aria-controls={id}
          onClick={() => setExpanded(v => !v)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

export default FeatureResponseView;
